import React, { useState } from "react";
import { getIcon } from "../utils/icons";
import { EditFieldGrid } from "./EditFieldGrid";
import { typography, surfaces, borders, textColors } from "../../design-system/tokens";

const EditGroup = ({
  groupName,
  groupInfo,
  fields,
  values,
  onChange,
  level = 0,
  parentFieldPath,
}) => {
  const [isOpen, setIsOpen] = useState(
    groupInfo?.initially_compressed !== true,
  );

  const sortedFields = fields.sort(
    (a, b) =>
      (a.metadata.appearance_order || 0) - (b.metadata.appearance_order || 0),
  );

  const toBool = (val) => {
    if (typeof val === "boolean") return val;
    if (typeof val === "number") return val !== 0;
    if (typeof val === "string") {
      return !["false", "0", "", "null", "undefined", "none"].includes(
        val.toLowerCase(),
      );
    }
    return Boolean(val);
  };

  const evalCondition = (cond) => {
    if (!cond) return null;

    let result = null;
    if (typeof cond === "string") {
      result = toBool(values[cond]);
    } else if (typeof cond === "object") {
      result = Object.entries(cond).every(([field, required]) => {
        const val = values[field];
        if (required === null || required === undefined) {
          return val === null || val === undefined;
        }
        if (typeof required === "boolean") {
          return toBool(val) === required;
        }
        return val === required;
      });
    }

    console.log("[EditGroup] evalCondition", groupName, "condition", {
      group: groupName,
      cond,
      valuesSnapshot: values,
      result,
    });

    return result;
  };

  // Determine if a field should be displayed based on show_if / hide_if
  // Default behaviour: show the field.
  // 1. If show_if condition evaluates strictly to false  -> hide
  // 2. If hide_if condition evaluates strictly to true   -> hide
  // Any other outcome (null or opposite boolean)         -> show
  const shouldShowField = (metadata) => {
    const appearance = metadata?.appearance;
    if (appearance === "hidden" || appearance === "readonly") return false;

    const { show_if, hide_if } = metadata;

    const showResult = evalCondition(show_if); // null | boolean
    const hideResult = evalCondition(hide_if); // null | boolean

    const shouldShow = !(showResult === false || hideResult === true);

    console.log("[EditGroup] shouldShowField", {
      group: groupName,
      field: metadata.name || "unknown",
      show_if: metadata.show_if,
      hide_if: metadata.hide_if,
      showResult,
      hideResult,
      shouldShow,
    });

    return shouldShow;
  };

  const visibleFieldsCount = sortedFields.filter(({ metadata }) =>
    shouldShowField(metadata),
  ).length;

  if (visibleFieldsCount === 0) return null;

  const handleToggle = () => setIsOpen(!isOpen);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleToggle();
    }
  };

  const safeGroupName =
    typeof groupName === "string" && groupName.trim()
      ? groupName
      : groupInfo?.title || "Group";
  const groupTitle = groupInfo?.title || safeGroupName;
  const groupDescription = groupInfo?.description;
  const groupIcon = groupInfo?.icon;
  const groupColor = groupInfo?.color;
  const isCompressible = groupInfo?.compressible !== false;

  return (
    <div
      className={`mb-3 ${
        level > 0 ? `ml-4 border-l pl-4 ${borders.default}` : ""
      }`}
    >
      {isCompressible ? (
        <button
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          aria-expanded={isOpen}
          aria-controls={`section-${safeGroupName
            .replace(/\s+/g, "-")
            .toLowerCase()}`}
          className={`w-full flex items-center justify-between p-3 text-left rounded-lg transition-all duration-200 border focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 ${
            level === 0
              ? `${surfaces.primary} ${surfaces.hover} ${textColors.primary} font-semibold ${borders.default} shadow-sm`
              : `${surfaces.secondary} hover:${surfaces.tertiary} ${textColors.secondary} font-medium ${borders.light}`
          } ${isOpen ? "shadow-md" : ""}`}
          style={
            groupColor
              ? { borderLeftColor: groupColor, borderLeftWidth: "4px" }
              : {}
          }
        >
          <div className="flex items-center space-x-3">
            <span
              className={`transition-transform duration-200 ${
                isOpen ? "rotate-90" : ""
              }`}
            >
              ▶
            </span>
            {groupIcon && (
              <div className={textColors.tertiary}>{getIcon(groupIcon)}</div>
            )}
            <div className="flex flex-col">
              <span>{groupTitle}</span>
              {groupDescription && (
                <span className={typography.caption}>
                  {groupDescription}
                </span>
              )}
            </div>
          </div>
          {visibleFieldsCount > 0 && (
            <span className={`px-2 py-1 ${typography.caption} bg-blue-100 text-blue-700 rounded-full`}>
              {visibleFieldsCount}
            </span>
          )}
        </button>
      ) : (
        <div className={`p-3 rounded-lg border ${borders.default} ${surfaces.primary}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {groupIcon && (
                <div className={textColors.tertiary}>{getIcon(groupIcon)}</div>
              )}
              <div className="flex flex-col">
                <span className="font-semibold">{groupTitle}</span>
                {groupDescription && (
                  <span className={typography.caption}>
                    {groupDescription}
                  </span>
                )}
              </div>
            </div>
            {visibleFieldsCount > 0 && (
              <span className={`px-2 py-1 ${typography.caption} bg-blue-100 text-blue-700 rounded-full`}>
                {visibleFieldsCount}
              </span>
            )}
          </div>
        </div>
      )}

      <div
        id={`section-${safeGroupName.replace(/\s+/g, "-").toLowerCase()}`}
        className={`transition-all duration-200 ${
          !isCompressible || isOpen
            ? "max-h-[80vh] overflow-y-auto opacity-100"
            : "max-h-0 overflow-hidden opacity-0"
        }`}
      >
        <EditFieldGrid
          className="mt-3 pb-2"
          fields={sortedFields}
          values={values}
          onChange={onChange}
          parentFieldPath={parentFieldPath}
        />
      </div>
    </div>
  );
};

export { EditGroup };
